import {
  type FocusEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type { LabeledOption } from '@fv/client-components/combobox'
import { useDebouncedValue } from '@fv/client-core'
import type { CfrHazardousMaterial } from '@fv/models'

import { ShipperAutocomplete } from '../../../components/inputs/ShipperAutocomplete'
import { type CfrHazmatField, isValidCfrFieldValue } from './cfrHazmat'
import { useCfrHazmatSearch } from './useCfrHazmatSearch'

const invalidValueMessages: Record<CfrHazmatField, string> = {
  properShippingName:
    'Select a valid proper shipping name from the list, or re-enter the UN/NA number to find a match.',
  identificationNumber:
    'Select a valid UN/NA number from the list, or re-enter the proper shipping name to find a match.',
}

type HazmatOption = LabeledOption<CfrHazardousMaterial>

type Props = {
  field: CfrHazmatField
  name: string
  value: string
  readOnly?: boolean
  disabled?: boolean
  required?: boolean
  onChange: (value: string) => void
  onSelectMaterial?: (material: CfrHazardousMaterial) => void
}

export const CfrHazmatTypeahead = ({
  field,
  name,
  value,
  readOnly,
  disabled,
  required,
  onChange,
  onSelectMaterial,
}: Props) => {
  const debouncedValue = useDebouncedValue(value)
  const searchQuery = useCfrHazmatSearch(field, debouncedValue)
  const confirmedValueRef = useRef(value)
  const validityRef = useRef<HTMLInputElement>(null)

  const detailField: CfrHazmatField =
    field === 'properShippingName'
      ? 'identificationNumber'
      : 'properShippingName'

  const options: HazmatOption[] = useMemo(
    () =>
      (searchQuery.data ?? []).map(material => ({
        label: material[field],
        value: material,
      })),
    [searchQuery.data, field],
  )

  const searchSettled = !searchQuery.isFetching && debouncedValue === value
  const isInvalidValue =
    !readOnly &&
    !disabled &&
    searchSettled &&
    value !== confirmedValueRef.current &&
    value.trim().length > 0 &&
    !isValidCfrFieldValue(field, value, searchQuery.data)

  useEffect(() => {
    validityRef.current?.setCustomValidity(
      isInvalidValue ? invalidValueMessages[field] : '',
    )
  }, [isInvalidValue, field])

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget)) return
    if (value === '') {
      confirmedValueRef.current = ''
    }
  }

  return (
    <div className="relative w-full" onBlur={handleBlur}>
      <ShipperAutocomplete
        name={name}
        value={value}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        isClearable
        isLoading={searchQuery.isFetching}
        filter={null}
        options={options}
        classNames={{ container: 'w-full' }}
        getOptionLabel={getHazmatOptionLabel}
        getOptionValue={getHazmatOptionValue}
        onValueChange={onChange}
        onSelect={option => {
          if (!option) return
          confirmedValueRef.current = option.value[field]
          if (onSelectMaterial) {
            onSelectMaterial(option.value)
          } else {
            onChange(option.value[field])
          }
        }}
        renderOption={option =>
          renderHazmatOption(option, field, detailField, value)
        }
      />
      <input
        aria-hidden="true"
        tabIndex={-1}
        className="pointer-events-none absolute left-0 top-0 -z-10 h-full w-full border-transparent bg-transparent opacity-0"
        ref={validityRef}
      />
    </div>
  )
}

const getHazmatOptionLabel = (option: HazmatOption) => option.label

const getHazmatOptionValue = (option: HazmatOption) =>
  option.value._id.toString()

const renderHazmatOption = (
  option: HazmatOption,
  field: CfrHazmatField,
  detailField: CfrHazmatField,
  query: string,
): ReactNode => {
  const { packingGroups, secondaryClass, tertiaryClass } = option.value
  const packingGroup = packingGroups.length
    ? `PG ${packingGroups.join(', ')}`
    : undefined

  const details = [
    option.value.primaryClass,
    secondaryClass,
    tertiaryClass,
    option.value[detailField],
    packingGroup,
  ]
    .filter(Boolean)
    .join(' • ')

  return (
    <div className="flex flex-col gap-0.5 pb-1.5">
      <span>{highlightMatch(option.value[field], query)}</span>
      {option.value.qualifier && (
        <span className="text-xs text-gray-600">{option.value.qualifier}</span>
      )}
      {details && <span className="text-xs text-gray-500">{details}</span>}
    </div>
  )
}

// Highlights the portion of the option text matching the user's search query so
// it is easy to see why each result matched.
const highlightMatch = (text: string, query: string): ReactNode => {
  const trimmed = query.trim()
  if (!trimmed) return text

  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const segments = text.split(new RegExp(`(${escaped})`, 'ig'))

  return segments.map((segment, index) =>
    segment.toLowerCase() === trimmed.toLowerCase() ? (
      <mark key={index} className="bg-transparent font-semibold text-inherit">
        {segment}
      </mark>
    ) : (
      segment
    ),
  )
}
